//Current version 3.1

class _PrivateFetchClass {
    constructor() {
        //---------------------Private attributes - END
        let accessTokenKey = null;
        let refreshTokenKey = null;
        let loginEndpoint = null;
        let logoutEndpoint = null;
        let refreshEndpoint = null;
        let logoutCallback = () => null;
        let urlReplacement = [];
        let globalUrlPrefix = '';
        let globalResponseCallback = response => response;
        let globalErrorCallback = error => {
            return {propagate: true, error};
        };
        let clientID = '_DefaultFetcherAppID';
        //---------------------Private attributes - END

        //---------------------Builder - START
        class FetchConfigurerStart {
            constructor() {
                this.setClientId = value => {
                    clientID = value;
                    return _PrivateFetch.configurer;
                };
            }
        }

        class FetchConfigurer {
            constructor() {
                this.setAccessTokenKey = value => {
                    accessTokenKey = value;
                    return _PrivateFetch.configurer;
                };
                this.setRefreshTokenKey = value => {
                    refreshTokenKey = value;
                    return _PrivateFetch.configurer;
                };
                this.setLoginEndpoint = value => {
                    loginEndpoint = value;
                    return _PrivateFetch.configurer;
                };
                this.setLogoutEndpoint = value => {
                    logoutEndpoint = value;
                    return _PrivateFetch.configurer;
                };
                this.setRefreshEndpoint = value => {
                    refreshEndpoint = value;
                    return _PrivateFetch.configurer;
                };
                this.setLogoutCallback = callback => {
                    logoutCallback = callback;
                    return _PrivateFetch.configurer;
                };
                this.addUrlReplacement = (oldValue, newValue) => {
                    //Remove any existing pair with oldValue
                    urlReplacement = urlReplacement.filter(el => el.old !== oldValue);
                    //add the new one
                    urlReplacement.push({old: oldValue, new: newValue});
                    return _PrivateFetch.configurer;
                };
                this.setGlobalUrlPrefix = urlPrefix => {
                    globalUrlPrefix = urlPrefix;
                    return _PrivateFetch.configurer;
                };
                this.setGlobalResponseCallback = callback => {
                    globalResponseCallback = callback;
                    return _PrivateFetch.configurer;
                };
                this.setGlobalErrorCallback = callback => {
                    globalErrorCallback = callback;
                    return _PrivateFetch.configurer;
                };
            }
        }

        this.configurerStep1 = new FetchConfigurerStart();
        this.configurer = new FetchConfigurer();
        //---------------------Builder - END

        //---------------------Public methods - START
        this.makeRequest = (url, method, data) => {
            return fetchWrapper(url, method, data).catch(error => {
                if (error.response.status === 401) {
                    //Expired token
                    //Can we try to refresh it?
                    if (refreshEndpoint) {
                        const currentAccessToken = getValue('AccessToken');
                        return mutex.use(() => tryRefresh(currentAccessToken, url, method, data));
                    } else {
                        //Expired token, no way to update it
                        _PrivateFetch.doLogout();
                    }
                }
                let ret = globalErrorCallback(error);
                if (ret && ret.propagate && ret.error !== undefined) {
                    throw ret.error;
                } else {
                    throw error;
                }
            });
        };
        this.doLogin = data => {
            if (!loginEndpoint) {
                //Call without an endpoint...you fool!
                return new Promise(() => {
                    const error = new Error('Missing login endpoint');
                    error.response = {};
                    throw error;
                });
            }
            return fetchWrapper(loginEndpoint, 'POST', {...data, grant_type: 'password', client_id: clientID}).then(
                interceptTokens
            );
        };
        this.doLogout = () => {
            const promise = () => {
               this.setValue('RefreshToken', null);
               this.setValue('AccessToken', null);
                if (logoutCallback) logoutCallback();
            };

            if (!logoutEndpoint || !getRefreshToken()) {
                return new Promise(promise);
            }
            return fetchWrapper(logoutEndpoint, 'POST')
                .then(promise)
                .catch(promise);
        };
        //---------------------Public methods - END

        //---------------------Service methods - START
        const dictionaryContainer = {};
        const getValue = key => dictionaryContainer[clientID + key];
        this.setValue = (key, value) => dictionaryContainer[clientID + key] = value;
        const getRefreshToken = () => {
            //Send refreshToken or old access token?
            const currentRefreshToken = getValue('RefreshToken');
            return currentRefreshToken !== null ? currentRefreshToken : getValue('AccessToken');
        };
        const fetchWrapper = (url, method, data) => {
            url = `${globalUrlPrefix}${url}`;

            // if not valid url
            if (url.search(/[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&/=]*)/) === -1) {
                // add origin
                url = window.location.origin + url;
            }

            urlReplacement.forEach(el => url = url.replace(el.old, el.new));

            const token = getValue('AccessToken');
            const params = {
                method,
                credentials: 'include',
                headers: {
                    Authorization: token ? 'Bearer ' + token : undefined
                }
            };

            if (method === 'GET') {
                url = new URL(url);
                // add data to query string
                if (data) {
                    if (!(data instanceof Array)) {
                        //Flattering needed
                        data = flatData(data);
                    }
                    url.search = new URLSearchParams(data);
                }
            } else {
                if (data instanceof FormData) {
                    params.body = data;
                } else {
                    params.body = JSON.stringify(data);
                    params.headers['Content-Type'] = 'application/json';
                }
            }

            return fetch(url, params)
                .then(checkResponseStatus)
                .then(globalResponseCallback)
                .then(response => response.text())
                .then(dat => (dat ? JSON.parse(dat) : {}));
        };
        const interceptTokens = data => {
            if (data[accessTokenKey]) {
                this.setValue('AccessToken', data[accessTokenKey]);
            }
            if (data[refreshTokenKey]) {
               this.setValue('RefreshToken', data[refreshTokenKey]);
            }
            return data;
        };

        const tryRefresh = (oldAccessToken, url, method, data) => {
            const refresh = getRefreshToken();
            const access = getValue('AccessToken');

            if (oldAccessToken === access) {
                return fetchWrapper(refreshEndpoint, 'POST', {
                    refresh_token: refresh,
                    grant_type: 'refresh_token',
                    client_id: clientID
                })
                    .then(interceptTokens)
                    .catch(refreshingError => {
                        _PrivateFetch.doLogout();
                        //Not an error to be managed here
                        throw refreshingError;
                    })
                    .then(() => {
                        //Data from refresh request ignored
                        //Try again with original request
                        return fetchWrapper(url, method, data);
                    });
            } else if (access !== null) {
                //Something already changed it
                return fetchWrapper(url, method, data);
            }
            //logout executed, no more to do
        };
        //---------------------Service methods - END
    }
}

//---------------------Exported methods - START
export function get(url, data) {
    return _PrivateFetch.makeRequest(url, 'GET', data);
}

export function post(url, data) {
    return _PrivateFetch.makeRequest(url, 'POST', data);
}

export function fetchCustomMethod(url, method, data) {
    return _PrivateFetch.makeRequest(url, method, data);
}

export function setAccessToken(newToken) {
    _PrivateFetch.setValue('AccessToken', newToken);
}

export function doLogin(data) {
    return _PrivateFetch.doLogin(data);
}

export function doLogout() {
    return _PrivateFetch.doLogout();
}

export function configureFetch() {
    return _PrivateFetch.configurerStep1;
}

//---------------------Exported methods - END

//---------------------Services - START
const _PrivateFetch = new _PrivateFetchClass();

function flatData(data) {
    //this function flats the data in order to avoid bad object to array conversions
    //Example
    //sent {foo: ["bar1", "bar2", "bar3"], another: "value".....}
    //received [ ["foo", "bar1"], ["foo", "bar2"], ["foo", "bar3"], [another: "value"] ]
    //Yes...it sucks!
    const retVal = [];
    Object.keys(data).forEach(key => {
        const val = data[key];
        if (val instanceof Array) {
            val.forEach(innerValue => retVal.push([key, innerValue]));
        } else {
            retVal.push([key, val]);
        }
    });
    return retVal;
}

function checkResponseStatus(response) {
    if (response.ok) {
        return response;
    } else {
        const error = new Error(response.statusText);
        error.response = response;
        throw error;
    }
}

//---------------------Services - END

class Semaphore {
    constructor(number) {
        let count = number;
        const tasks = [];

        const sched = () => {
            if (count > 0 && tasks.length > 0) {
                count--;
                let next = tasks.shift();
                if (next === undefined) {
                    throw 'Unexpected undefined value in tasks list';
                }

                next();
            }
        };

        this.acquire = () => {
            return new Promise(res => {
                const task = () => {
                    let released = false;
                    res(() => {
                        if (!released) {
                            released = true;
                            count++;
                            sched();
                        }
                    });
                };
                tasks.push(task);
                if (process && process.nextTick) {
                    process.nextTick(sched.bind(this));
                } else {
                    setImmediate(sched.bind(this));
                }
            });
        };

        this.use = funct => {
            return this.acquire().then(release => {
                return funct()
                    .then(res => {
                        release();
                        return res;
                    })
                    .catch(err => {
                        release();
                        throw err;
                    });
            });
        };
    }
}

class Mutex extends Semaphore {
    constructor() {
        super(1);
    }
}

const mutex = new Mutex();
